import {
  Box,
  Button,
  Grid,
  InputLabel,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Select,
  Typography,
  Checkbox,
  FormControl,
  Paper,
  ListItemText,
} from "@mui/material";
import { useState, useCallback } from "react";
import { styled } from "@mui/material/styles";
import api from "../../../services/CBT";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontFamily: "Gilroy-Bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Gilroy-Regular",
  },
}));

const AddQuestion = ({ onSuccess, id, title, subject, subjectId }) => {
  const { admToken, user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [refId, setRefId] = useState(
    `${Date.now()}${Math.floor(Math.random() * 1000)}`
  );

  const [formData, setFormData] = useState({
    question: "",
    questionRef: `reference${refId}`,
    subjectId: subjectId,
    schoolId: user?.schoolId,
    classId: 0,
    answer: "",
    correctOptions: [],
    setUpId: id,
    correctAnswer: "",
    correctOption: "",
    answerType: "single",
    options: {
      optionA: "",
      optionB: "",
      optionC: "",
      optionD: "",
      optionE: "",
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOptionChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        [name]: value,
      },
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const addCBTAttachment = useCallback(async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('SchoolId', user?.schoolId);
      formData.append('FileName', `${title}_${subject}`);
      formData.append('FileContent', selectedFile);
      formData.append('QuestionRef', `reference${refId}`);

      const res = await api.addCBTAttachment(admToken, formData);

      if (res.status && res.statusCode === 2) {
        toast.success(res.message || "Uploaded successfully");
      } else {
        toast.error(res.message || "Failed to upload attachment");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload attachment");
    } finally {
      setUploading(false);
    }
  }, [admToken, selectedFile, title, subject, user?.schoolId]);

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      let payload = {
        question: formData.question,
        questionRef: `reference${refId}`,
        subjectId: formData.subjectId,
        schoolId: formData.schoolId,
        classId: formData.classId,
        setUpId: formData.setUpId,
        answerType: "",
        answer: "",
        correctOptions: "",
        correctOption: "",
        correctAnswer: "",
        options: {
          optionA: formData.options.optionA,
          optionB: formData.options.optionB,
          optionC: formData.options.optionC,
          optionD: formData.options.optionD,
          optionE: formData.options.optionE,
        },
      };

      switch (formData.answerType) {
        case "single":
          payload.answerType = "S";
          payload.correctOption = formData.correctOptions[0];
          payload.answer = formData.options[`option${payload.correctOption}`];
          break;

        case "multiple":
          payload.answerType = "M";
          payload.correctOptions = formData.correctOptions.join(",");
          payload.answer = formData.correctOptions
            .map((opt) => formData.options[`option${opt}`])
            .join(", ");
          break;

        case "fill":
          payload.answerType = "F";
          payload.correctAnswer = formData.correctAnswer;
          payload.answer = formData.correctAnswer;
          break;

        default:
          throw new Error("Please select an answer type");
      }

      const res = await api.addCBTQuestions(admToken, payload);

      if (res.status && res.statusCode === 2) {
        toast.success(res.message || "Question Successfully Added", {
          autoClose: 4000,
          position: toast.POSITION.BOTTOM_LEFT,
        });
        const newRefId = `${Date.now()}${Math.floor(Math.random() * 1000)}`;
        setRefId(newRefId);
        setFormData({
          question: "",
          questionRef: `reference${newRefId}`,
          subjectId: subjectId,
          schoolId: user?.schoolId,
          classId: 0,
          answer: "",
          correctOptions: [],
          setUpId: id,
          correctAnswer: "",
          correctOption: "",
          answerType: formData.answerType,
          options: {
            optionA: "",
            optionB: "",
            optionC: "",
            optionD: "",
            optionE: "",
          },
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to add question");
    } finally {
      setLoading(false);
    }
  }, [admToken, formData, user?.schoolId, subjectId, onSuccess]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item lg={12} xs={12}>
          <Box>
            <InputLabel
              sx={{
                mb: 1.5,
                mt: 1,
                fontSize: "12px",
                fontWeight: "bolder",
                color: "#000",
                fontFamily: "Gilroy-Medium",
              }}
            >
              Enter Question In the textBox Below
            </InputLabel>
            <TextField
              name="question"
              fullWidth
              size="small"
              value={formData.question}
              onChange={handleChange}
              multiline
              minRows={2}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "1px solid #671E78",
                  },
                  "& textarea": {
                    minHeight: "80px",
                  },
                  padding: "8px",
                },
              }}
              placeholder="Enter your question here"
            />
          </Box>
        </Grid>
        <Grid item lg={12} xs={12}>
          <Box>
            <InputLabel
              sx={{
                mb: 1.5,
                mt: 1,
                fontSize: "12px",
                fontWeight: "bolder",
                color: "#000",
                fontFamily: "Gilroy-Medium",
              }}
            >
              Upload Picture (Optional)
            </InputLabel>

            <input
              type="file"
              id="file-upload"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div className="flex items-center gap-5 w-full">
              <label htmlFor="file-upload"
                className="w-[80%]"
              >
                <Button
                  variant="outlined"
                  component="span"
                  sx={{
                    border: "1px solid #671E78",
                    color: "#671E78",
                    textTransform: "initial",
                    width: "100%",
                    "&:hover": {
                      border: "1px solid #671E78",
                      backgroundColor: "rgba(103, 30, 120, 0.04)",
                    },
                  }}
                >
                  {selectedFile ? selectedFile.name : "Choose File"}
                </Button>
              </label>
              <Button
                variant="contained"
                sx={{
                  textTransform: "initial",
                  backgroundColor: "#671E78",
                  width: "30%",
                  "&:hover": {
                    backgroundColor: "#4e1661",
                  },
                }}
                onClick={addCBTAttachment}
                disabled={uploading || loading || !selectedFile}
              >
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>

      <Grid item lg={12} xs={12}>
        <Box sx={{ mb: 3, mt: 5 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "Gilroy-Medium",
              color: "#000",
              mb: 1,
            }}
          >
            Answer Type
          </Typography>
          <Box sx={{ display: "flex", gap: 2, backgroundColor: "rgba(103, 30, 120, 0.1)", p: "8px" }}>
            {["single", "multiple", "fill"].map((type) => (
              <Button
                key={type}
                variant={formData.answerType === type ? "contained" : "outlined"}
                onClick={() =>
                  setFormData({
                    ...formData,
                    answerType: type,
                    correctOptions: type === "single" ? [formData.correctOptions[0] || ""] : [],
                  })
                }
                sx={{
                  textTransform: "initial",
                  borderColor: "#671E78",
                  color: formData.answerType === type ? "#fff" : "#671E78",
                  backgroundColor:
                    formData.answerType === type ? "#671E78" : "transparent",
                  "&:hover": {
                    backgroundColor:
                      formData.answerType === type
                        ? "#4e1661"
                        : "rgba(103, 30, 120, 0.04)",
                  },
                }}
              >
                {type === "single" && "Single Option"}
                {type === "multiple" && "Multiple Choice"}
                {type === "fill" && "Fill in the Gap"}
              </Button>
            ))}
          </Box>
        </Box>

        {formData.answerType !== "fill" && (
          <TableContainer
            component={Paper}
            sx={{ mb: 3, border: "1px solid #671E78", backgroundColor: "ffffff", boxShadow: "none" }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: "ffffff" }}>
                <TableRow>
                  <TableCell
                    sx={{ fontFamily: "Gilroy-Medium", fontWeight: "bold", backgroundColor: "#ffffff" }}
                  >
                    Option
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: "Gilroy-Medium", fontWeight: "bold", backgroundColor: "#ffffff" }}
                  >
                    Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {["A", "B", "C", "D", "E"].map((option) => (
                  <TableRow key={option} sx={{ backgroundColor: "#ffffff", boxShadow: "none" }}>
                    <TableCell sx={{ fontFamily: "Gilroy-Regular", backgroundColor: "#ffffff" }}>
                      Option {option}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Gilroy-Regular", backgroundColor: "#ffffff" }}>
                      <TextField
                        fullWidth
                        size="small"
                        name={`option${option}`}
                        value={formData.options[`option${option}`] || ""}
                        onChange={handleOptionChange}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { border: "1px solid #671E78" },
                          },
                          backgroundColor: "#ffffff"
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily: "Gilroy-Medium" }}>
                  {formData.answerType === "multiple"
                    ? "Select Correct Options"
                    : "Select Correct Option"}
                </InputLabel>
                <Select
                  multiple={formData.answerType === "multiple"}
                  value={
                    formData.answerType === "multiple"
                      ? formData.correctOptions
                      : formData.correctOptions[0] || ""
                  }
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      correctOptions:
                        formData.answerType === "multiple"
                          ? e.target.value
                          : [e.target.value],
                    });
                  }}
                  sx={{ fontFamily: "Gilroy-Regular" }}
                  size="small"
                  renderValue={
                    formData.answerType === "multiple"
                      ? (selected) => selected.join(", ")
                      : undefined
                  }
                >
                  {["A", "B", "C", "D", "E"].map((option) => (
                    <MenuItem
                      key={option}
                      value={option}
                      sx={{ fontFamily: "Gilroy-Regular" }}
                    >
                      {formData.answerType === "multiple" && (
                        <Checkbox
                          checked={formData.correctOptions.includes(option)}
                          sx={{
                            color: "#671E78",
                            "&.Mui-checked": { color: "#671E78" },
                          }}
                        />
                      )}
                      <ListItemText primary={`Option ${option}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </TableContainer>
        )}

        {formData.answerType === "fill" && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  mb: 1,
                  fontSize: "12px",
                  fontWeight: "bolder",
                  color: "#000",
                  fontFamily: "Gilroy-Medium",
                }}
              >
                Correct Answer
              </InputLabel>
              <TextField
                fullWidth
                size="small"
                name="correctAnswer"
                value={formData.correctAnswer}
                onChange={handleChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { border: "1px solid #671E78" },
                  },
                }}
              />
            </Box>
          </Box>
        )}

        <Box mt={5}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              textTransform: "initial",
              backgroundColor: "#671E78",
              "&:hover": { backgroundColor: "#4e1661" },
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Processing..." : "Add Question"}
          </Button>
        </Box>
      </Grid>
    </Box>
  );
};

export default AddQuestion;